/* Opt-in site-wide smooth scrolling for same-page anchor links.
 * Wheel and touch scrolling remain native for predictable input performance. */
(() => {
  'use strict';
  if (window.__mipSmoothScrollInstalled || !window.MipSmoothScrollConfig || !window.requestAnimationFrame) return;
  window.__mipSmoothScrollInstalled = true;

  const config = window.MipSmoothScrollConfig;
  const number = (value, fallback, min, max) => {
    value = Number(value);
    return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
  };
  const settings = {
    duration: number(config.duration, 550, 100, 2500),
    speed: number(config.speed, 100, 25, 300) / 100,
    offset: number(config.offset, 0, 0, 500),
    easing: ['ease_out_cubic', 'ease_in_out_cubic', 'linear'].includes(config.easing) ? config.easing : 'ease_out_cubic',
    interruptible: config.interruptible !== false,
    reduceMotion: () => Boolean(config.respectReducedMotion && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  };
  let frame = 0;
  let removeInterrupts = () => {};
  let restoreScrollBehavior = () => {};
  const scrollY = () => window.scrollY || window.pageYOffset || 0;
  const maxY = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const ease = (p) => settings.easing === 'linear' ? p : settings.easing === 'ease_in_out_cubic' ? (p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2) : 1 - Math.pow(1 - p, 3);
  const stop = () => { if (frame) window.cancelAnimationFrame(frame); frame = 0; removeInterrupts(); removeInterrupts = () => {}; restoreScrollBehavior(); restoreScrollBehavior = () => {}; };
  const focus = (target) => {
    if (!target || target === document.body) return;
    const temporary = !target.hasAttribute('tabindex');
    if (temporary) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (temporary) target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
  };
  const animate = (destination, target) => {
    stop();
    const start = scrollY(), distance = destination - start;
    if (Math.abs(distance) < 2 || settings.reduceMotion()) { window.scrollTo(0, destination); focus(target); return; }
    // CSS native smooth scrolling is enabled separately for browser-managed
    // navigation. Disable it for this animation's frames so the configured
    // duration remains exact instead of two animations fighting each other.
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    restoreScrollBehavior = () => { root.style.scrollBehavior = previousScrollBehavior; };
    const factor = Math.min(1.7, Math.max(.35, Math.abs(distance) / Math.max(window.innerHeight, 1)));
    const duration = Math.min(2500, Math.max(100, settings.duration * factor / settings.speed));
    const started = performance.now();
    if (settings.interruptible) {
      const abort = () => stop();
      const keydown = (event) => { if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Escape', ' '].includes(event.key)) abort(); };
      window.addEventListener('wheel', abort, { passive: true, once: true });
      window.addEventListener('touchstart', abort, { passive: true, once: true });
      window.addEventListener('keydown', keydown);
      removeInterrupts = () => { window.removeEventListener('wheel', abort); window.removeEventListener('touchstart', abort); window.removeEventListener('keydown', keydown); };
    }
    const tick = (now) => {
      const progress = Math.min(1, (now - started) / duration);
      window.scrollTo(0, Math.round(start + distance * ease(progress)));
      if (progress < 1) frame = window.requestAnimationFrame(tick);
      else { frame = 0; removeInterrupts(); removeInterrupts = () => {}; restoreScrollBehavior(); restoreScrollBehavior = () => {}; focus(target); }
    };
    frame = window.requestAnimationFrame(tick);
  };
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download') || link.dataset.noSmoothScroll !== undefined) return;
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    let url; try { url = new URL(href, window.location.href); } catch (_) { return; }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
    let target; try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))) || document.querySelector(url.hash); } catch (_) { return; }
    if (!target) return;
    event.preventDefault();
    const destination = Math.min(maxY(), Math.max(0, scrollY() + target.getBoundingClientRect().top - settings.offset));
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
    animate(destination, target);
  });

  const initScrollReveal = () => {
    if (config.revealEnabled === false || settings.reduceMotion() || !('IntersectionObserver' in window)) return;
    const main = document.getElementById('MainContent');
    if (!main) return;
    const revealStyle = (['fade_up', 'fade', 'slide_left', 'slide_right', 'scale'].includes(config.revealStyle) ? config.revealStyle : 'fade_up').replace(/_/g, '-');
    const duration = number(config.revealDuration, 600, 100, 2500);
    const distance = number(config.revealDistance, 28, 0, 160);
    const threshold = number(config.revealThreshold, 10, 0, 80) / 100;
    const replay = config.revealReplay === true;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const section = entry.target;
        if (entry.isIntersecting) {
          section.classList.remove('mip-scroll-reveal-pending');
          section.classList.add('mip-scroll-reveal-visible');
          if (!replay) observer.unobserve(section);
        } else if (replay && section.classList.contains('mip-scroll-reveal-visible')) {
          section.classList.remove('mip-scroll-reveal-visible');
          section.classList.add('mip-scroll-reveal-pending');
        }
      });
    }, { threshold, rootMargin: '0px 0px -6% 0px' });

    const pending = new Set();
    let registrationFrame = 0;
    let revealReady = false;
    const flushSections = () => {
      registrationFrame = 0;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const measured = Array.from(pending).filter(section => main.contains(section)).map(section => ({ section, rect: section.getBoundingClientRect() }));
      pending.clear();
      if (!revealReady && measured.length) {
        revealReady = true;
        document.documentElement.classList.add('mip-scroll-reveal-enabled');
        document.documentElement.style.setProperty('--mip-reveal-duration', duration + 'ms');
        document.documentElement.style.setProperty('--mip-reveal-distance', distance + 'px');
      }
      measured.forEach(({ section, rect }) => {
        section.dataset.mipScrollRevealRegistered = 'true';
        if (rect.top < viewportHeight || rect.height >= viewportHeight) return;
        section.classList.add('mip-scroll-reveal', 'mip-scroll-reveal--' + revealStyle, 'mip-scroll-reveal-pending');
        observer.observe(section);
      });
    };
    const register = (section) => {
      if (!section || section.dataset.mipScrollRevealRegistered === 'true' || section.closest('[data-mip-no-scroll-reveal]')) return;
      // Shopify normally wraps each section in this ID, but some template
      // layouts add an extra wrapper. Match both forms anywhere in <main>.
      if (!section.matches('[id^="shopify-section-"], .shopify-section')) return;
      pending.add(section);
      if (!registrationFrame) registrationFrame = window.requestAnimationFrame(flushSections);
    };
    const registerSections = (root) => {
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      register(root);
      root.querySelectorAll('[id^="shopify-section-"], .shopify-section').forEach(register);
    };
    registerSections(main);
    new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach(registerSections))).observe(main, { childList: true, subtree: true });
  };

  // Reveals are cosmetic: CSS keeps pending content visible even if observer
  // delivery is delayed or interrupted by another script.
  window.requestAnimationFrame(initScrollReveal);
})();
